import React from 'react';
import {
  WyscoutAnalyticsDashboard,
  WyscoutAnalyticsFilters,
  WyscoutAnalyticsLens,
  wyscoutAnalyticsLenses,
} from '@/features/players/wyscoutAnalyticsApi';
import {LoadingSpinner} from '@/components/ui/LoadingStatus';

interface Props {
  filters: WyscoutAnalyticsFilters;
  options: WyscoutAnalyticsDashboard['filterOptions'];
  pendingLens?: WyscoutAnalyticsLens | null;
  onChange: (filters: WyscoutAnalyticsFilters) => void;
}

const lensLabel: Record<WyscoutAnalyticsLens, string> = {
  forward: 'Forward',
  midfielder: 'Midfielder',
  defender: 'Defender',
  goalkeeper: 'Goalkeeper',
};

const matchLabel = (match: WyscoutAnalyticsDashboard['filterOptions']['matches'][number]): string => (
  `${match.opponent}, ${match.competition}, ${new Date(match.date).toLocaleDateString('en-GB', {timeZone: 'UTC'})}`
);

const AnalyticsToolbar: React.FC<Props> = ({filters, options, pendingLens = null, onChange}) => {
  const matches = filters.seasonId === null
    ? options.matches
    : options.matches.filter(match => match.seasonId === filters.seasonId);
  const selected = new Set(filters.matchIds);
  const selectLensByKeyboard = (event: React.KeyboardEvent<HTMLButtonElement>, currentLens: WyscoutAnalyticsLens) => {
    const current = wyscoutAnalyticsLenses.indexOf(currentLens);
    let next = current;
    if (event.key === 'ArrowRight' || event.key === 'ArrowDown') next = (current + 1) % wyscoutAnalyticsLenses.length;
    if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') next = (current - 1 + wyscoutAnalyticsLenses.length) % wyscoutAnalyticsLenses.length;
    if (event.key === 'Home') next = 0;
    if (event.key === 'End') next = wyscoutAnalyticsLenses.length - 1;
    if (next === current) {
      if (['Home', 'End'].includes(event.key)) event.preventDefault();
      return;
    }
    event.preventDefault();
    const nextLens = wyscoutAnalyticsLenses[next];
    document.getElementById(`analytics-lens-${nextLens}`)?.focus();
    onChange({...filters, lens: nextLens});
  };

  return <div className="space-y-4 rounded-lg bg-gray-50 p-4 ring-1 ring-gray-200 dark:bg-gray-950 dark:ring-gray-800">
    <div>
      <p className="mb-2 text-sm font-semibold text-gray-900 dark:text-gray-100">Role lens</p>
      <p id="analytics-lens-description" className="mb-2 text-xs text-gray-500 dark:text-gray-400">Choose the role used to organise this analysis. This does not change the player's stored position.</p>
      <div role="radiogroup" aria-label="Role lens" aria-describedby="analytics-lens-description" className="flex flex-wrap gap-2">
        {wyscoutAnalyticsLenses.map(lens => <button
          key={lens}
          id={`analytics-lens-${lens}`}
          type="button"
          role="radio"
          aria-label={pendingLens === lens ? `${lensLabel[lens]} analytics loading` : undefined}
          aria-checked={filters.lens === lens}
          tabIndex={filters.lens === lens ? 0 : -1}
          className={`min-h-11 rounded-md border px-3 text-sm font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-club-blue-500 ${filters.lens === lens ? 'border-club-blue-600 bg-club-blue-600 text-white' : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-100 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-200 dark:hover:bg-gray-800'}`}
          onClick={() => { if (filters.lens !== lens) onChange({...filters, lens}); }}
          onKeyDown={event => selectLensByKeyboard(event, lens)}
        ><span className="inline-flex items-center gap-2">
          {pendingLens === lens && <LoadingSpinner className="h-4 w-4 text-current" />}
          {lensLabel[lens]}
        </span></button>)}
      </div>
      <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">Viewing {lensLabel[filters.lens]} analytics</p>
    </div>

    <div className="grid min-w-0 gap-4 lg:grid-cols-[minmax(12rem,0.7fr)_minmax(0,1.3fr)]">
      <label className="block text-sm font-semibold text-gray-900 dark:text-gray-100">
        Season
        <select
          aria-label="Season"
          className="input-field mt-2 block min-h-11 w-full"
          value={filters.seasonId ?? ''}
          onChange={event => onChange({...filters, seasonId: event.target.value ? Number(event.target.value) : null, matchIds: []})}
        >
          <option value="">All seasons</option>
          {options.seasons.map(season => <option key={season.id} value={season.id}>{season.label}</option>)}
        </select>
      </label>

      <fieldset className="min-w-0">
        <legend className="text-sm font-semibold text-gray-900 dark:text-gray-100">Matches</legend>
        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">Choose up to 100 matches. Leave all unchecked to include every available match.</p>
        <div className="mt-2 max-h-52 space-y-1 overflow-y-auto rounded-md border border-gray-300 bg-white p-2 dark:border-gray-700 dark:bg-gray-900">
          {matches.length === 0 && <p className="px-2 py-3 text-sm text-gray-600 dark:text-gray-300">No matches available for this selection.</p>}
          {matches.map(match => {
            const label = matchLabel(match);
            return <label key={match.id} className="flex min-h-11 cursor-pointer items-start gap-3 rounded px-2 py-2 text-sm text-gray-800 hover:bg-gray-50 dark:text-gray-200 dark:hover:bg-gray-800">
              <input
                type="checkbox"
                aria-label={label}
                className="mt-0.5 h-5 w-5 shrink-0 rounded border-gray-300 text-club-blue-600 focus:ring-club-blue-500 dark:border-gray-600 dark:bg-gray-800"
                checked={selected.has(match.id)}
                disabled={!selected.has(match.id) && filters.matchIds.length >= 100}
                onChange={() => onChange({
                  ...filters,
                  matchIds: selected.has(match.id)
                    ? filters.matchIds.filter(id => id !== match.id)
                    : [...filters.matchIds, match.id],
                })}
              />
              <span className="min-w-0 break-words">{label}</span>
            </label>;
          })}
        </div>
        {filters.matchIds.length > 0 && <button type="button" className="mt-2 min-h-11 rounded-md px-2 text-sm font-semibold text-club-blue-700 hover:bg-club-blue-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-club-blue-500 dark:text-club-blue-300 dark:hover:bg-gray-800" onClick={() => onChange({...filters, matchIds: []})}>Clear selected matches</button>}
      </fieldset>
    </div>
  </div>;
};

export default AnalyticsToolbar;
